// src/components/mafia/action/MafiaActionScreen.js

import React, { useState, useMemo, useEffect } from 'react';
import useStore from '../../../store';
import { useTranslation } from 'react-i18next';
import { FaCrosshairs, FaTint, FaHeart, FaSearch, FaBriefcase, FaCommentDots, FaSkull } from 'react-icons/fa';
import './MafiaActionScreen.css';

// ИЗМЕНЕНИЕ: Принимаем prop isLocked
const MafiaActionScreen = ({ isLocked }) => {
  const { t } = useTranslation();

  // ДОБАВИТЬ ЭТУ СТРОКУ: Подписка на обновления рендера
  useStore(state => state.render);

  const store = useStore.getState();
  const { ref } = store;
  const { mafia, work, telegram, info } = ref;
  const myId = telegram.telegramUserId;
  const { role, team } = mafia;
  const game = info.game;
  const mafiaPublic = game?.mafiaPublic || {};

  // 1. Читаем данные из Store
  const nightState = mafia.nightState || {};
  const { mafiaState, myActions } = nightState;

  // 2. ИСПРАВЛЕНИЕ: Точный расчет доступных действий
  const availableActions = useMemo(() => {
      const actions = [];

      // Мафия (Только если ты в команде мафии!)
      if (team === 'mafia') {
          actions.push({ type: 'mafia_vote', label: 'mafia.action_shoot', icon: <FaCrosshairs /> });
      }

      // Доктор
      if (role === 'doctor') {
          actions.push({ type: 'heal', label: 'mafia.action_heal', icon: <FaHeart /> });
      }

      // Детектив
      if (role === 'detective') {
          actions.push({ type: 'interrogate', label: 'mafia.action_interrogate', icon: <FaSearch /> });
      }

      // Адвокат (пример будущего мульти-действия: и стреляет как мафия, и защищает)
      if (role === 'lawyer') {
          // У него уже добавился 'mafia_vote' выше, добавляем второе
          actions.push({ type: 'lawyer_protect', label: 'mafia.action_protect', icon: <FaBriefcase /> });
      }

      return actions;
  }, [role, team]);

  const [currentActionType, setCurrentActionType] = useState(availableActions[0]?.type || null);

  // Обновляем текущий тип, если он вдруг стал недоступен (например при смене роли в дебаге)
  useEffect(() => {
      if (availableActions.length > 0 && !availableActions.find(a => a.type === currentActionType)) {
          setCurrentActionType(availableActions[0].type);
      }
  }, [availableActions, currentActionType]);

  // Если действий несколько, рисуем переключатель (можно добавить UI переключателя)
  // Для простоты, если действий > 1, можно добавить табы в header.

  // --- ДАННЫЕ ---
  const serverTargetId = myActions[currentActionType];
  const isMafiaAction = currentActionType === 'mafia_vote';

  // Для мафии берем глобальный статус, но на UI это влияет только информативно (счетчик)
  // Кнопка "Завершить" отправляет личный сигнал

  // Обработчик клика по игроку
  const handlePlayerClick = (targetId) => {
      // ИСПРАВЛЕНИЕ: Мафия может менять выбор всегда
      // Доктор/Детектив тоже (пока ночь не кончилась)
      work.methods.hostAction('mafiaNightAction', {
          type: currentActionType,
          targetId: targetId
      });
  };

  const handleBlob = (targetId) => {
      work.methods.hostAction('mafiaNightAction', { type: 'mafia_blob', targetId });
  };

  const handleFinish = () => {
      if (isMafiaAction) {
          // Отправляем сигнал готовности (даже повторно)
          work.methods.hostAction('mafiaNightAction', { type: 'mafia_finish' });
          // Переходим в чат
          mafia.methods.setActiveTab('chat_mafia');
      } else {
          // Для Доктора -> Миниигра
          if (role === 'doctor') {
              mafia.methods.setActiveTab('minigame');
          }
          // Для Детектива -> ждем сервер (он сам откроет экран) или можно закрыть меню
          // Но по ТЗ он "ждет".
      }
  };

  // ИСПРАВЛЕНИЕ: Логика кнопки Назад / Оверлея
  const handleBack = () => {
      // ИЗМЕНЕНИЕ: Если заблокировано (критическая ситуация 5 сек), запрещаем выход
      if (isLocked) return;

      if (team === 'mafia') {
          mafia.methods.setActiveTab('chat_mafia');
      } else {
          mafia.methods.setActiveTab('draw'); // Или 'watch'
      }
  };

  const handleOverlayClick = (e) => {
    // ИЗМЕНЕНИЕ: Запрещаем закрытие по клику на оверлей, если locked
    if (isLocked) return;
    if (e.target === e.currentTarget) handleBack();
  };

  // --- СОРТИРОВКА СПИСКА ---
  const rawPlayers = Object.values(info.game?.users || {});
  const sortedPlayers = [...rawPlayers].sort((a, b) => {
      const teams = mafiaPublic.teams || {};
      const alive = mafiaPublic.alive || {};

      const isAliveA = alive[a.id] !== false;
      const isAliveB = alive[b.id] !== false;

      // 1. Сначала живые
      if (isAliveA && !isAliveB) return -1;
      if (!isAliveA && isAliveB) return 1;

      // 2. Если Я МАФИЯ -> Моя команда сверху
      if (team === 'mafia') {
          const isMafiaA = teams[a.id] === 'mafia';
          const isMafiaB = teams[b.id] === 'mafia';
          if (isMafiaA && !isMafiaB) return -1;
          if (!isMafiaA && isMafiaB) return 1;
      }

      return 0; // Иначе как есть (по ID или порядку добавления)
  });

  // ИСПРАВЛЕНИЕ: Текст и статус кнопки
  // Кнопка активна, ТОЛЬКО если выбор сделан (serverTargetId существует)
  const isButtonDisabled = !serverTargetId;

  let buttonText = t('mafia.action_select_target'); // "Выберите цель" (по дефолту)
  if (serverTargetId) {
      if (isMafiaAction) buttonText = t('mafia.finish_round'); // "Завершить"
      else buttonText = t('mafia.action_confirm'); // "Подтвердить" (или "Вылечить"/"Допросить")
  }

  return (
    <div className="mafia-action-screen-container" onClick={handleOverlayClick}>
        <div className="mafia-action-card">
            <div className="shot-menu-header">
                {/* ИСПРАВЛЕНИЕ: Табы в виде пилюль */}
                {availableActions.length > 1 ? (
                    <div className="action-tabs">
                        {availableActions.map(act => (
                            <button
                                key={act.type}
                                className={`action-tab-btn ${currentActionType === act.type ? 'active' : ''}`}
                                onClick={() => setCurrentActionType(act.type)}
                            >
                                {act.icon} {t(act.label)}
                            </button>
                        ))}
                    </div>
                ) : (
                    <span className="shot-menu-title">
                        {availableActions[0]?.icon} {t(availableActions[0]?.label || 'mafia.action_generic')}
                    </span>
                )}

                {isMafiaAction && (
                    <div className="shot-menu-subtitle">
                        {t('mafia.waiting_for_mafia', { count: mafiaState?.readyForNightEnd?.length || 0 })}
                    </div>
                )}
            </div>

            <div className="shot-menu-list add-scroll touch-active">
              {sortedPlayers.map(player => {
                const isAlive = (mafiaPublic.alive || {})[player.id] !== false;
                const isMe = String(player.id) === String(myId);

                // Проверка на сокомандника (для стиля)
                // Если я мафия, и у этого игрока team === 'mafia'
                const isMafiaTeammate = team === 'mafia' && (mafiaPublic.teams || {})[player.id] === 'mafia';

                // --- ЛОГИКА ОТКЛЮЧЕНИЯ (DISABLED) ---
                // 1. Если мертв
                // 2. Если я Детектив и это Я сам (Детектив не допрашивает себя)
                const isDetectiveSelf = role === 'detective' && isMe;
                const isDisabled = !isAlive || isDetectiveSelf;

                // --- ЛОГИКА ВЫБОРА ---
                // Обводим (selected), ТОЛЬКО если это МОЙ выбор
                const isSelectedByMe = String(serverTargetId) === String(player.id);

                // --- ЛОГИКА МАФИИ ---
                let voteCount = 0;
                let isBlob = false;
                if (isMafiaAction && mafiaState) {
                    voteCount = Object.values(mafiaState.shootVotes).filter(id => String(id) === String(player.id)).length;
                    isBlob = String(mafiaState.blobTarget) === String(player.id);
                }

                return (
                    <div
                        key={player.id}
                        className={`player-item
                            ${isMafiaTeammate ? 'is-mafia' : ''}
                            ${isSelectedByMe ? 'selected' : ''}
                            ${isDisabled ? 'disabled' : ''}
                        `}
                        onClick={() => !isDisabled && handlePlayerClick(player.id)}
                    >
                        <div className="target-icon-container">
                             {currentActionType === 'heal' ? <FaHeart className="target-icon"/> :
                              currentActionType === 'interrogate' ? <FaSearch className="target-icon"/> :
                              (isMafiaTeammate ? <FaSkull className="target-icon" style={{opacity: 0.5}}/> : <FaCrosshairs className="target-icon"/>)}
                             
                             {voteCount > 0 && <div className="vote-count-badge">{voteCount}</div>}
                        </div>

                        <div className="player-info">
                            <span className="player-name">{player.name} {isMe ? '(Вы)' : ''}</span>
                            {isMafiaTeammate && <span className="player-role-label">MAFIA</span>}
                        </div>

                        {/* Клякса доступна и для своих (isMafiaTeammate), и для себя */}
                        {isMafiaAction && isAlive && (
                            <div className="actions-container" onClick={(e) => e.stopPropagation()}>
                                <div className={`action-btn-icon ${isBlob ? 'active' : ''}`} onClick={() => handleBlob(player.id)}>
                                    <FaTint size={14} />
                                </div>
                            </div>
                        )}
                    </div>
                );
              })}
            </div>

            <div className="shot-menu-footer">
                {/* ИЗМЕНЕНИЕ: Скрываем кнопку Назад, если isLocked (или делаем неактивной и прозрачной) */}
                {!isLocked && (
                    <button className="back-btn" onClick={handleBack}>
                        <FaCommentDots />
                    </button>
                )}

                {/* ИСПРАВЛЕНИЕ: Логика активности кнопки и текст */}
                <button
                    className={`finish-btn ${!isButtonDisabled ? 'active' : ''}`}
                    onClick={!isButtonDisabled ? handleFinish : undefined}
                >
                    {buttonText}
                </button>
            </div>
        </div>
    </div>
  );
};

export default MafiaActionScreen;
